import { communityChannel, communityPage } from '~publish/legacy/routes'

type BuildPostRouteParams = {
  postId: string
  isMultiChannel: boolean
  channelId?: string
}

/**
 * Builds the navigation route for selecting a post.
 * Handles routing differences between:
 * - Multi-channel vs single-channel views
 *
 * @returns The route string, or null if unable to build (missing required channelId)
 */
export function buildPostRoute({
  postId,
  isMultiChannel,
  channelId,
}: BuildPostRouteParams): string | null {
  if (isMultiChannel) {
    return communityPage.getViewRoute({ view: 'posts', postId })
  }

  if (!channelId) {
    return null
  }

  return communityChannel.getRoute({ channelId, view: 'posts', postId })
}

/**
 * The comment-details dialog over the by-post view, with the comment's post
 * selected behind it so closing lands on that post. The dialog moves a shared
 * link here itself once it knows the post; copied links always use
 * `commentDetailsRoute.getRoute`.
 */
export function buildCommentDetailsByPostRoute({
  postId,
  commentId,
}: {
  postId: string
  commentId: string
}): string {
  const postRoute = communityPage.getViewRoute({
    view: 'posts',
    postId,
    copiedSearchParams: [],
  })
  return `${postRoute}/comment/${commentId}`
}

type BuildPostsListRouteParams = Omit<BuildPostRouteParams, 'postId'>

/**
 * Builds the route for the posts list view (no post selected).
 * Use this instead of passing `postId: ''` to `buildPostRoute` so the
 * intent is explicit when navigating back to the list.
 */
export function buildPostsListRoute({
  isMultiChannel,
  channelId,
}: BuildPostsListRouteParams): string | null {
  return buildPostRoute({
    postId: '',
    isMultiChannel,
    channelId,
  })
}
